'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import iban from 'iban';

import {
    InputMask,
    InputMaskProps,
    InputMaskChangeEvent
} from 'primereact/inputmask';

import {__} from '~/shared/src/utilities/messageCatalog';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {ibanRenderer} from '~/frontend/src/utilities/valueRenderers';
import {StoreState} from '~/frontend/src/store';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';


type InputProps = Omit<InputMaskProps, 'autoComplete' | 'onChange' | 'key'>;
const inputProps = keys<InputProps>();

type InputChangeProps = InputMaskChangeEvent;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * Should the value shown as defined (raw) <strong>or</strong> should they be formatted.
     */
    rawMode? : boolean,

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    markDirty      : false,
    rawMode        : false,
    inputClassNames: [],
    inputStyle     : {},
    useHookForm    : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// outside the default values are not required (AllProps),
// but inside they are required (set to default) for static type checking (AllPropsWithDefaultsSet)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const IbanField = React.forwardRef<InputMask, ComponentProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => state.session?.user );

        const {
                  uid,
                  autoComplete,
                  disabled,
                  id,
                  inputClassNames,
                  inputStyle,
                  markDirty,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  pt,
                  rawMode,
                  useHookForm,
                  value
              }                                 = props,
              elementId                         = id ?? `${uid}_ibanField`,
              inputRef                          = useRef<InputMask | null>( null ),
              inValue                           = useRef<string | undefined>( value ),
              outValue                          = useRef<string | undefined>( value ),
              timeoutId                         = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ] = useState<string | undefined>( value ),
              [ focus, setFocus ]               = useState<boolean>( false ),
              messageResolver                   = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      JSON.stringify( messagePlaceholders ),
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder               = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              fieldWrapperAutoCompleteConfig    = useMemo(
                  () => {
                      let retVal : Partial<FieldWrapperComponentProps> = {};

                      if ( autoComplete && !disabled ) {
                          retVal = {
                              autoCompleteValue                  : elementValue,
                              autoCompleteInputHasFocus          : focus,
                              onAutoCompleteValueChange          : ( autoCompleteValueRaw : string, originalEvent : any ) => {
                                  const autoCompleteValue = autoCompleteValueRaw.replace( ' ', '' );

                                  if ( autoCompleteValue !== elementValue ) {
                                      setElementValue( autoCompleteValue );

                                      onChange( autoCompleteValue, originalEvent );
                                  }
                              },
                              autoCompleteSuggestionsItemTemplate: ( suggestion : string ) => {
                                  return ibanRenderer( suggestion, undefined, rawMode );
                              }
                          };
                      }

                      return retVal;
                  },
                  [
                      autoComplete,
                      disabled,
                      elementValue,
                      focus,
                      onChange
                  ]
              ),
              mask                              = useMemo(
                  () => {
                      let retVal : string = 'aa99******************************';

                      // build mask
                      if ( elementValue && elementValue.length > 1 ) {
                          const normalizedValue = elementValue.replace( /[^a-zA-Z0-9]/g, '' ).toUpperCase(),
                                countryCode     = normalizedValue.slice( 0, 2 );

                          if ( iban.countries[ countryCode ]?.structure ) {
                              const parsedMask = iban.countries[ countryCode ].structure.match( /(.{3})/g )?.map( function( block ) {
                                  // parse each structure block (1-char (pattern) + 2-digits (repeats))
                                  const pattern = block.slice( 0, 1 ),
                                        repeats = parseInt( block.slice( 1 ), 10 );

                                  let maskChar : string = '';

                                  // map pattern to mask char
                                  switch ( pattern ) {
                                      case 'A': // 0-9A-Za-z
                                      case 'B': // 0-9A-Z
                                      case 'W': // 0-9a-z
                                          maskChar = '*';
                                          break;
                                      case 'C': // A-Za-z
                                      case 'L': // a-z
                                      case 'U': // A-Z
                                          maskChar = 'a';
                                          break;
                                      case 'F': // 0-9
                                          maskChar = '9';
                                          break;
                                  }

                                  return maskChar ? maskChar.repeat( repeats ) : '';
                              } ) ?? [];

                              retVal = `aa99${parsedMask.join( '' )}`;
                          }

                          if ( !rawMode ) {
                              retVal = retVal.replace( /(.{4})(?!$)/g, '$1 ' );
                          }
                      }

                      return retVal;
                  },
                  [
                      elementValue,
                      rawMode
                  ]
              ),
              onInputChange                     = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = ( event.target.value ?? '' ).toUpperCase();

                      if ( changedValue !== outValue.current && !( changedValue === '' && outValue.current === undefined ) ) {
                          // paste text handling
                          //    if country code changed
                          //        -> set elementValue to empty string
                          //            - this resets the mask (since current mask can block invalid chars for current country's IBAN structure)
                          //                - this allows any input
                          //         -> the onPaste event handler will set the elementValue to pasted text delayed (this will even trigger this handler again)
                          if ( ( outValue.current ?? '' ).length > 4 && changedValue.length > 4 && changedValue.slice( 0, 2 ) !== outValue.current?.slice( 0, 2 ) ) {
                              setElementValue( '' );
                          }

                          // normal handling
                          else {
                              setElementValue( changedValue );

                              onChange( changedValue, event );
                          }
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange             = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = ( event.target.value ?? '' ).toUpperCase();

                      if ( changedValue !== outValue.current && !( changedValue === '' && outValue.current === undefined ) ) {
                          // paste text handling
                          //    if country code changed
                          //        -> set elementValue to empty string
                          //            - this resets the mask (since current mask can block invalid chars for current country's IBAN structure)
                          //                - this allows any input
                          //         -> the onPaste event handler will set the elementValue to pasted text delayed (this will even trigger this handler again)
                          if ( ( outValue.current ?? '' ).length > 4 && changedValue.length > 4 && changedValue.slice( 0, 2 ) !== outValue.current?.slice( 0, 2 ) ) {
                              setElementValue( '' );
                          }

                          // normal handling
                          else {
                              setElementValue( changedValue );

                              onIntermediateChange?.( changedValue, event );

                              if ( timeoutId.current ) {
                                  clearTimeout( timeoutId.current );
                              }
                              timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                          }
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                      = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                       = useCallback(
                  ( event : React.FocusEvent<HTMLInputElement> ) => {
                      setFocus( false );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onInputPaste                      = useCallback(
                  ( event : React.ClipboardEvent<HTMLInputElement> ) => {
                      const changedValue = event.clipboardData?.getData( 'Text' ) ?? '';

                      // if country code changed set elementValue to pasted text delayed (see onInputChange / onHookFormInputChange)
                      if ( ( outValue.current ?? '' ).length > 4 && changedValue.length > 4 && changedValue.slice( 0, 2 ) !== outValue.current?.slice( 0, 2 ) ) {
                          setTimeout(
                              () => {
                                  setElementValue( changedValue );
                              },
                              12
                          );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              fixCursorPosition                 = useCallback(
                  () => {
                      const currentValue       = ( inputRef.current as unknown as HTMLInputElement )?.value ?? '',
                            lastValidCharIndex = currentValue.match( /[ \w\p{L}\p{N}]*/u )?.[ 0 ].length;

                      let selectionStart = ( inputRef.current as unknown as HTMLInputElement )?.selectionStart,
                          selectionEnd   = ( inputRef.current as unknown as HTMLInputElement )?.selectionEnd;

                      if ( lastValidCharIndex !== undefined && selectionStart !== null && selectionEnd !== null ) {
                          if ( selectionStart > lastValidCharIndex ) {
                              selectionStart = lastValidCharIndex;
                          }
                          if ( selectionEnd > lastValidCharIndex ) {
                              selectionEnd = lastValidCharIndex;
                          }

                          ( inputRef.current as unknown as HTMLInputElement )?.setSelectionRange?.( selectionStart, selectionEnd );
                      }
                  },
                  [
                      'onlyOnce'
                  ]
              );

        inValue.current = value;
        outValue.current = elementValue;

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}

            {...fieldWrapperAutoCompleteConfig}
            pt={pt?.fieldWrapper}
        >
            <InputMask
                {..._.pick( props, inputProps ) as InputProps}

                id={elementId}

                ref={( node ) => {
                    inputRef.current = node;
                    if ( typeof forwardedRef === 'function' ) {
                        forwardedRef( node );
                    }
                    else if ( forwardedRef ) {
                        forwardedRef.current = node;
                    }
                }}
                value={elementValue}

                onChange={useHookForm ? onHookFormInputChange : onInputChange}

                pt={{
                    root: {
                        onFocus         : onInputFocus,
                        onBlur          : onInputBlur,
                        onKeyUpCapture  : fixCursorPosition,
                        onMouseUpCapture: fixCursorPosition,
                        onPaste         : onInputPaste
                    }
                }}

                mask={mask}
                unmask={true}
                slotChar={'\u200b'}
                autoClear={false}

                className={`yn-ibanfield ${inputClassNames.join( ' ' )} ${rawMode ? 'yn-rawmode' : ''}`}
                style={{ ...defaultProps.inputStyle, ...inputStyle }}

                placeholder={resolvedPlaceholder}
                tooltip={undefined}
                tooltipOptions={undefined}

                aria-describedby={`${elementId}_message`}

                autoComplete={autoComplete && !disabled ? 'off' : 'on'}
                aria-autocomplete={autoComplete && !disabled ? 'list' : undefined}
                aria-controls={autoComplete && !disabled ? `${elementId}_autocomplete_list` : undefined}
                aria-haspopup={autoComplete && !disabled ? 'listbox' : undefined}
            />
        </FieldWrapper>;
    }
} );
IbanField.defaultProps = defaultProps;
IbanField.displayName = 'IbanField';

export default IbanField;
